import * as React from "react"
import { cva } from "class-variance-authority"
import { ChevronDown, ChevronLeft, ChevronRight } from "lucide-react"
import { DayPicker, type DayButtonProps, type DropdownProps } from "react-day-picker"
import { ptBR } from "react-day-picker/locale"

import { cn } from "../lib/cn"
import { buttonVariants } from "./Button"

const dayVariants = cva(
  "inline-flex size-8 items-center justify-center rounded-md text-sm font-normal transition-colors cursor-pointer outline-none focus-visible:border-ring focus-visible:ring-[3px] focus-visible:ring-ring/50",
  {
    variants: {
      state: {
        normal: "hover:bg-accent hover:text-accent-foreground",
        today: "font-semibold text-primary hover:bg-accent hover:text-accent-foreground",
        selected:
          "bg-primary text-primary-foreground hover:bg-primary hover:text-primary-foreground",
        outside: "text-muted-foreground hover:bg-accent hover:text-accent-foreground",
        disabled: "cursor-not-allowed text-muted-foreground opacity-50 pointer-events-none",
      },
    },
    defaultVariants: { state: "normal" },
  }
)

function dayState(modifiers: DayButtonProps["modifiers"], outside: boolean) {
  if (modifiers.disabled) return "disabled"
  if (modifiers.selected) return "selected"
  if (modifiers.today) return "today"
  if (outside) return "outside"
  return "normal"
}

function CalendarDayButton({ day, modifiers, className: _className, ...props }: DayButtonProps) {
  const nome = props["aria-label"]
  return (
    <button
      className={dayVariants({ state: dayState(modifiers, day.outside) })}
      {...props}
      aria-label={nome}
      title={nome}
    />
  )
}

function CalendarDropdown({ className: _className, options, ...props }: DropdownProps) {
  return (
    <span className="relative inline-flex items-center">
      <select
        className="cursor-pointer appearance-none rounded-sm bg-transparent py-0.5 pr-5 pl-2 text-sm font-semibold text-foreground outline-none focus-visible:ring-2 focus-visible:ring-ring"
        {...props}
      >
        {options?.map((option) => (
          <option key={option.value} value={option.value} disabled={option.disabled}>
            {option.label}
          </option>
        ))}
      </select>
      <ChevronDown
        size={12}
        className="pointer-events-none absolute right-1 text-muted-foreground"
        aria-hidden="true"
      />
    </span>
  )
}

const NAV_WINDOW_YEARS = 10

function defaultMonthWindow(today: Date) {
  const year = today.getFullYear()
  return {
    startMonth: new Date(year - NAV_WINDOW_YEARS, 0, 1),
    endMonth: new Date(year + NAV_WINDOW_YEARS, 11, 1),
  }
}

function Calendar({
  className,
  classNames,
  showOutsideDays = true,
  ...props
}: React.ComponentProps<typeof DayPicker>) {
  return (
    <DayPicker
      showOutsideDays={showOutsideDays}
      className={cn("p-3", className)}
      captionLayout="dropdown"
      navLayout="around"
      {...defaultMonthWindow(new Date())}
      classNames={{
        root: "w-fit",
        months: "flex flex-col gap-4",
        month: "grid grid-cols-[auto_1fr_auto] items-center gap-1",
        button_previous: cn(
          buttonVariants({ variant: "ghost", size: "icon-sm" }),
          "order-1"
        ),
        button_next: cn(
          buttonVariants({ variant: "ghost", size: "icon-sm" }),
          "order-3"
        ),
        month_caption: "order-2 flex items-center justify-center text-sm font-semibold",
        dropdowns: "flex flex-nowrap items-center justify-center gap-2",
        month_grid: "order-4 col-span-3 w-full border-collapse",
        weekdays: "flex items-stretch justify-stretch",
        weekday:
          "flex size-8 flex-1 items-center justify-center text-xs font-medium text-muted-foreground",
        week: "flex w-full gap-1 mt-1",
        day: "text-center text-sm p-0 relative",
        hidden: "invisible",
        ...classNames,
      }}
      components={{
        Root: ({ className, rootRef, ...rootProps }) => (
          <div
            data-slot="calendar"
            className={cn(className)}
            ref={rootRef}
            {...rootProps}
          />
        ),
        Chevron: ({ className, orientation, ...chevronProps }) => {
          const Icon = orientation === "right" ? ChevronRight : ChevronLeft
          return <Icon className={cn("size-4", className)} {...chevronProps} />
        },
        DayButton: CalendarDayButton,
        Dropdown: CalendarDropdown,
      }}
      {...props}
      locale={ptBR}
    />
  )
}

export { Calendar }
